import './global.css';
import '@aws-amplify/ui-react/styles.css';
import 'react-toastify/dist/ReactToastify.min.css';

import { Authenticator, ThemeProvider, useAuthenticator } from '@aws-amplify/ui-react';
import { translations } from '@aws-amplify/ui-react';
import CssBaseline from '@mui/material/CssBaseline';
import { confirmSignUp, signIn, signUp } from 'aws-amplify/auth';
import { I18n } from 'aws-amplify/utils';
import * as React from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import ConfirmResetPassword from '@/Authenticator/ConfirmResetPassword';
import SignIn from '@/Authenticator/SignIn';
import ResetPasswordPage from '@/pages/ResetPassword';

import { routes } from './app-routes';
import { Header } from './Authenticator/Header';
import { AppShell } from './components/AppShell';
import { AccountLockoutGuard } from './components/AccountLockoutGuard';
import { ErrorBoundary } from './components/ErrorBoundary';
import InitialLoader from './components/Loader/InitialLoader';
import MaintenanceGuard from './components/MaintenanceGuard';
import { ModalProvider } from './components/ModalLoading/context';
import { AccountLockoutProvider } from './contexts/AccountLockoutContext';
import useMixPanel from './hooks/useMixPanel';
import AccountSettings from './pages/AccountSettings';
import { getUserInfo } from './pages/AccountSettings/api';
import Home from './pages/Home';
import ReleaseNotes from './pages/ReleaseNotes';
import ReportPage from './pages/report';
import StructureForm from './Structures/Create';
import { useStructures } from './Structures/useStructures';
import { Structure } from './Structures/View';
import { Unit } from './Units/View';
import { useAdminAccess } from './useAdminAccess';

I18n.putVocabularies(translations);
I18n.setLanguage('en');

I18n.putVocabularies({
  en: {
    Confirm: 'Log in',
    Confirming: 'Log in',
  },
});

const App = (): JSX.Element => {
  const { structures } = useStructures();

  React.useEffect(() => {
    setTimeout(() => {
      window.history.replaceState('', document.title, window.location.pathname);
    });
  }, []);

  return (
    <ErrorBoundary>
      <React.Fragment>
        <ToastContainer
          closeButton={true}
          closeOnClick={false}
          draggable={true}
          pauseOnHover={true}
          hideProgressBar={false}
          newestOnTop={true}
          rtl={false}
          pauseOnFocusLoss={true}
          toastStyle={{
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
          }}
        />
        <CssBaseline />
        {!structures ? (
          <InitialLoader text={'Loading'} />
        ) : (
          <Routes>
            <Route path="/accounts" element={<AppShell hideDrawer />}>
              <Route path="reset-password" element={<ResetPasswordPage />} />
            </Route>

            <Route path="/account" element={<AppShell />}>
              <Route path="settings" element={<AccountSettings />} />
            </Route>

            <Route path="/account/settings" element={<AppShell />}>
              <Route path="return" element={<AccountSettings />} />
            </Route>

            <Route path="/account/settings" element={<AppShell />}>
              <Route path="refresh" element={<AccountSettings />} />
            </Route>

            <Route path="/release-notes" element={<AppShell />}>
              <Route index element={<ReleaseNotes />} />
            </Route>

            <Route
              path="/"
              element={
                <>
                  <AppShell />
                  {structures && structures?.length && structures?.length === 0 ? (
                    <StructureForm />
                  ) : null}
                </>
              }
            >
              <Route index element={<Home />} />
              <Route path={routes.unit} element={<Unit />} />
              <Route path={routes.property} element={<Structure />} />
            </Route>
          </Routes>
        )}
      </React.Fragment>
    </ErrorBoundary>
  );
};

function AuthenticatedApp() {
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);

  return (
    <AccountLockoutProvider>
      <MaintenanceGuard authState={authStatus}>
        <AccountLockoutGuard authState={authStatus}>
          <App />
        </AccountLockoutGuard>
      </MaintenanceGuard>
    </AccountLockoutProvider>
  );
}

export default function RootApp() {
  const { trackEvent, setUser } = useMixPanel();
  const adminAccess = useAdminAccess();
  const isAdminHash = window.location.hash.includes('admin');

  const isAdmin = isAdminHash && adminAccess.isAdminLogin;

  React.useEffect(() => {
    if (window.location.hash.includes('admin')) {
      if (!adminAccess.isAdminLogin) {
        adminAccess.updateAdminInfo({ adminEmail: adminAccess.adminEmail, isAdminLogin: true });
      } else {
        adminAccess.updateAdminInfo({ adminEmail: adminAccess.adminEmail, isAdminLogin: false });
        setTimeout(() => {
          window.history.replaceState('', document.title, window.location.pathname);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const services = {
    async handleSignIn(formData) {
      let { username, password } = formData;
      const adminInfo = JSON.parse(window.localStorage.getItem('adminInfo') || '{}');
      const isAdminLogin = adminInfo.isAdminLogin;
      const adminInputField: HTMLInputElement = document.getElementById(
        'admin-email',
      ) as HTMLInputElement;
      const adminEmail = adminInputField ? adminInputField.value : '';
      try {
        const res = await signIn({
          username,
          password,
          options: {
            authFlowType: 'USER_PASSWORD_AUTH',
          },
        });
        if (isAdminLogin) {
          window.localStorage.setItem('adminInfo', JSON.stringify({ ...adminInfo, adminEmail }));
        }
        if (res.isSignedIn) {
          const userInfo = await getUserInfo();
          setUser(userInfo.user.id, { $email: userInfo.user.email, $name: userInfo.user.email });
          await trackEvent('User Login', { id: userInfo.user.id });
        }
        return res;
      } catch (err) {
        throw err;
      }
    },
    async handleSignUp(formData) {
      let { username, password, options } = formData;
      const signUpResult = await signUp({
        username,
        password,
        options,
      });
      setUser(signUpResult.userId, { $email: username, $name: username });
      await trackEvent('User Created', { id: signUpResult.userId });
      return signUpResult;
    },
    async handleConfirmSignUp(formdata) {
      let { username, confirmationCode } = formdata;
      try {
        const confirmSignUpResult = await confirmSignUp({
          username,
          confirmationCode,
        });
        await trackEvent('User Confirmed');
        return confirmSignUpResult;
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
  };

  const formFields = isAdmin
    ? {
        signIn: {
          adminEmail: {
            labelHidden: true,
            placeholder: 'Enter Your Admin Email Here',
            isRequired: true,
            type: 'email',
            // @ts-ignore
            id: 'admin-email',
          },
        },
      }
    : undefined;

  // Debug admin access (development only)
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.log('Admin access check:', { isAdminLogin: adminAccess.isAdminLogin, isAdminHash });
  }

  return (
    <ThemeProvider>
      <ModalProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/report/:reportID" element={<AppShell hideDrawer />}>
              <Route path="" element={<ReportPage />} />
            </Route>
            <Route
              path="*"
              element={
                <Authenticator
                  components={{
                    Header,
                    ConfirmResetPassword: { Header: ConfirmResetPassword.Header },
                    SignIn: { Header: isAdmin ? SignIn.Header : undefined },
                    ConfirmSignUp: {
                      Header() {
                        return (
                          <div>
                            <h1 style={{ textAlign: 'center' }}>Check your email</h1>
                            <p style={{ textAlign: 'center' }}>
                              Please input your one-time access code below to confirm your email. It
                              may take a few minutes to arrive.
                            </p>
                          </div>
                        );
                      },
                      Footer() {
                        return (
                          <span style={{ color: '#787f85', fontSize: '12px', textAlign: 'center' }}>
                            Didn't receive the email? Check out our{' '}
                            <a
                              href="https://simplesubwatersupport.zendesk.com/hc/en-us/articles/41858536891291-Email-Delivery-Troubleshooting"
                              rel="noopener noreferrer"
                              target="_blank"
                              style={{ color: '#787f85' }}
                            >
                              troubleshooting guide.
                            </a>
                          </span>
                        );
                      },
                    },
                  }}
                  loginMechanisms={['email']}
                  variation="modal"
                  services={services}
                  hideSignUp={isAdmin}
                  formFields={formFields}
                >
                  <AuthenticatedApp />
                </Authenticator>
              }
            />
          </Routes>
        </BrowserRouter>
      </ModalProvider>
    </ThemeProvider>
  );
}

export { App };
